export const DEFAULT_RESPONSE_FORMAT_OPTIONS = [
  'Default',
  'Yes/No',
  'Bullet Points',
  'Number',
  'Custom',
] as const;

type SupportedResponseFormat = (typeof DEFAULT_RESPONSE_FORMAT_OPTIONS)[number];

const RESPONSE_FORMAT_API_KEYS = [
  'responseFormatOptions',
  'responseTypeOptions',
  'responseTypes',
  'outputOptions',
] as const;

const RESPONSE_FORMAT_VALUE_KEYS = [
  'value',
  'label',
  'name',
  'responseType',
  'responseFormat',
] as const;

const supportedResponseFormats = new Set<string>(DEFAULT_RESPONSE_FORMAT_OPTIONS);

const getNormalizedResponseFormatFromString = (
  value: string,
): SupportedResponseFormat | '' => {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return '';
  }

  const normalizedValue = trimmedValue.toLowerCase();
  if (normalizedValue === 'default' || normalizedValue === 'paragraph' || normalizedValue === 'text') {
    return 'Default';
  }
  if (normalizedValue === 'yes/no' || normalizedValue === 'yesno' || normalizedValue === 'boolean') {
    return 'Yes/No';
  }
  if (
    normalizedValue === 'bullet points' ||
    normalizedValue === 'bulletpoints' ||
    normalizedValue === 'bullet'
  ) {
    return 'Bullet Points';
  }
  if (normalizedValue === 'number' || normalizedValue === 'numerical') {
    return 'Number';
  }
  if (normalizedValue === 'custom' || normalizedValue === 'custom format') {
    return 'Custom';
  }

  return supportedResponseFormats.has(trimmedValue) ? (trimmedValue as SupportedResponseFormat) : '';
};

const getNormalizedResponseFormatFromUnknown = (
  value: unknown,
): SupportedResponseFormat | '' => {
  if (typeof value === 'string') {
    return getNormalizedResponseFormatFromString(value);
  }

  if (!value || typeof value !== 'object') {
    return '';
  }

  const valueRecord = value as Record<string, unknown>;
  for (const key of RESPONSE_FORMAT_VALUE_KEYS) {
    const candidateValue = valueRecord[key];
    if (typeof candidateValue === 'string') {
      const normalizedValue = getNormalizedResponseFormatFromString(candidateValue);
      if (normalizedValue) {
        return normalizedValue;
      }
    }
  }

  return '';
};

export const normalizeResponseFormat = (value?: unknown): SupportedResponseFormat | '' => {
  return getNormalizedResponseFormatFromUnknown(value);
};

export const getResponseFormatLabel = (value?: unknown): string => {
  const normalizedValue = normalizeResponseFormat(value);
  if (!normalizedValue) {
    return '';
  }

  if (normalizedValue === 'Default') return 'Paragraph';
  if (normalizedValue === 'Custom') return 'Custom Format';
  return normalizedValue;
};

export const extractResponseFormatOptions = (payload?: unknown): SupportedResponseFormat[] => {
  if (!payload || typeof payload !== 'object') {
    return [...DEFAULT_RESPONSE_FORMAT_OPTIONS];
  }

  const payloadRecord = payload as Record<string, unknown>;
  const extractedOptions = RESPONSE_FORMAT_API_KEYS.flatMap((key) => {
    const options = payloadRecord[key];
    return Array.isArray(options) ? options : [];
  })
    .map((option) => getNormalizedResponseFormatFromUnknown(option))
    .filter(Boolean) as SupportedResponseFormat[];

  const uniqueOptions = Array.from(new Set(extractedOptions));

  return uniqueOptions.length > 0 ? uniqueOptions : [...DEFAULT_RESPONSE_FORMAT_OPTIONS];
};
